import { apiClient } from './apiClient';

export type UpsellStage =
  | 'identified'
  | 'contacted'
  | 'proposal_discussed'
  | 'closed_won'
  | 'closed_lost';

export interface UpsellOpportunity {
  id: string;
  tenant: string;
  contract: string | null;
  lead: string | null;
  client: string | null;
  contract_lead: string;
  lead_email: string;
  lead_name: string;
  name: string;
  description: string | null;
  potential_value: string;
  stage: UpsellStage;
  expected_close_date: string | null;
  converted_at: string | null;
  converted_contract: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsellCreatePayload {
  contract_id?: string | null;
  lead_id?: string | null;
  client_id?: string | null;
  name: string;
  description?: string | null;
  potential_value: string;
  stage: UpsellStage;
  expected_close_date?: string | null;
}

export interface UpsellConvertPayload {
  contract_type: 'one_time' | 'recurring';
  recurring_interval?: 'monthly' | 'quarterly' | 'yearly';
  start_date: string;
  end_date?: string | null;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const upsellService = {
  async getUpsells(params?: {
    page?: number;
    stage?: string;
  }): Promise<PaginatedResponse<UpsellOpportunity>> {
    const queryParams: Record<string, string> = {};
    if (params?.page) queryParams.page = String(params.page);
    if (params?.stage) queryParams.stage = params.stage;
    return apiClient.get('/revenue/upsells/', queryParams);
  },

  async getUpsell(id: string): Promise<UpsellOpportunity> {
    return apiClient.get(`/revenue/upsells/${id}/`);
  },

  async createUpsell(payload: UpsellCreatePayload): Promise<UpsellOpportunity> {
    return apiClient.post('/revenue/upsells/', payload);
  },

  async updateUpsell(id: string, data: Partial<UpsellOpportunity>): Promise<UpsellOpportunity> {
    return apiClient.patch(`/revenue/upsells/${id}/`, data);
  },

  async deleteUpsell(id: string): Promise<void> {
    return apiClient.delete(`/revenue/upsells/${id}/`);
  },

  async convertUpsell(id: string, payload: UpsellConvertPayload): Promise<{ contract_id: string }> {
    return apiClient.post(`/revenue/upsells/${id}/convert/`, payload);
  },

  async getContracts(): Promise<{ count: number; results: { id: string; lead_email: string; lead_name: string; total_value: string }[] }> {
    return apiClient.get('/revenue/contracts/');
  },

  async getLeads(params?: { page?: number }): Promise<PaginatedResponse<{ id: string; email: string; first_name: string; last_name: string }>> {
    const queryParams: Record<string, string> = {};
    if (params?.page) queryParams.page = String(params.page);
    return apiClient.get('/leads/', queryParams);
  },

  /** All pages of legacy crm.Lead rows (form/marketing leads). */
  async getAllLeads(): Promise<{ id: string; email: string; first_name: string; last_name: string }[]> {
    const all: { id: string; email: string; first_name: string; last_name: string }[] = [];
    let page = 1;
    while (true) {
      const res = await this.getLeads({ page });
      all.push(...(res.results || []));
      if (!res.next) break;
      page += 1;
    }
    return all;
  },
};
